import React, {useEffect, useState} from "react";
import {Link as RouteLink} from 'react-router-dom';
import AppBar from "@material-ui/core/AppBar";
import {ErrorScreen, FuseAnimate, HeaderToolbar, Loading} from "../../components";
import {Link} from "@material-ui/core";
import {Typography} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import Button from "@material-ui/core/Button";
import {Call, Email} from "@material-ui/icons";
import clsx from "clsx";
import {useStore} from "../../store";
import {fetchAccount} from "../../store/actions";
import {useAuthUser} from "../../components/auth-provider";
import {FAQAccordion} from "../../components";
import {ReactComponent as Logo} from "../../images/phonethru-logo.svg";


const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        justifyContent: "center",
    },
    bodyText: {
        fontWeight  : 300,
    },
    emphasis: {
        fontWeight: 700,
    },
    para: {
        fontWeight  : 300,
        marginBottom: 16,
    },
    link: {
        color: '#653ab1',
        fontWeight: 700,
        '&:hover': {
            textDecoration: 'underline',
            textDecorationColor: '#653ab1',
        },
    },
    list: {
        fontWeight: 300,
        listStyle: 'disc outside none',
        padding: '10px 10px 10px 25px',
    },
    panelLogo: {
        width: 200,
        height: 51,
    },
}));

const SupportPage = () => {
    const classes = useStyles();
    const {user} = useAuthUser();          // Get logged in user
    const [store, dispatch] = useStore();   // Get the global store

    // Set up local state
    const [state, setState] = useState({
        property : '',
        tocLinks : [],
    })

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    // Make sure the account is available in the store
    useEffect(()=> {
        dispatch(fetchAccount(user.accountId, store, dispatch))
    }, [user.accountId, store, dispatch])

    // Set the Table of Contents links
    useEffect(()=>{
        if (store.account.property) {
            setState({
                property: store.account.property,
                // tocLinks: tocLinks
            })
        }
    }, [store.account.property])

    return (
        <div id="support-root" className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <HeaderToolbar location="support"/>
            </AppBar>
            <Toolbar />

            <main className={classes.content}>

                { store.account.isFetching ? <Loading/> : null }

                { store.account.error && <ErrorScreen error={store.account.error}/> }

                { store.account.error === null && !store.account.isFetching ?
                    <div className="flex flex-grow flex-col mt-4">
                        <FuseAnimate animation="transition.expandIn" delay={100}>
                            <ContactCard accountId={user.accountId}/>
                        </FuseAnimate>

                        { state.property === 'phonethru' ?
                            <PhoneThruSupport
                                accountId={user.accountId}
                                property={state.property}
                                accountType={store.account.type || ''}
                                autoRecharge={store.account.autoRecharge || {}}
                            />
                            : null
                        }
                    </div>
                    : null
                }
            </main>
        </div>
    );
}

const PhoneThruSupport = ({accountId, property, autoRecharge, accountType}) => {
    const classes = useStyles();

    return (
        <React.Fragment>
            <FuseAnimate animation="transition.expandIn" delay={200}>
                <div id="hc-changes" className="mb-6">
                    <Typography variant="h6">Mobile & Number Changes</Typography>

                    <FAQAccordion title="Can I change my mobile number?">
                        <MobileNumberChangeContent property={property}/>
                    </FAQAccordion>

                    <FAQAccordion title="Adding a new landline number">
                        <div className="flex flex-col">
                            <div className={classes.para}>
                                To add another landline number to your account tap the <strong>Add Number</strong> button under the <strong>My
                                Numbers</strong> section on the Home page, or tap the button below.
                            </div>
                            <div className={classes.para}>
                                There is a one-time <span className={classes.emphasis}>$9</span> set up fee for a new landline number.
                            </div>
                            <div>
                                <Button
                                    component={RouteLink}
                                    variant="contained"
                                    color="secondary"
                                    to="/transfer"
                                >
                                    Add A Landline Number
                                </Button>
                            </div>

                        </div>
                    </FAQAccordion>
                </div>
            </FuseAnimate>

            <FuseAnimate animation="transition.expandIn" delay={300}>
                <div id="hc-changes" className="mb-6">
                    <Typography variant="h6">Landline Numbers</Typography>

                    <FAQAccordion title="Stopping calls temporarily">
                        <div className="flex flex-col">
                            <div className={classes.para}>
                                To 'turn off' a landline number tap it on the Home page to show the <strong>Manage Number</strong> button
                                and tap the button to go to the service page.
                            </div>
                            <div className={classes.para}>
                                In the Call Forwarding section you'll see a
                                switch.  Turn the switch off to stop receiving calls.  You can follow the same process to
                                turn Call Forwarding back on when you're ready to receive calls again.
                            </div>
                            <div className={classes.para}>
                                Turning Call Forwarding off does not affect the expiry of the call minutes on the landline number.
                            </div>
                            <div className={classes.para}>
                                Calls to your landline number are rejected when call forwarding is turned off.
                                Your caller will hear a 'busy' tone.
                            </div>
                            <div className={classes.para}>
                                You do not use call minutes for rejected calls.
                            </div>
                        </div>
                    </FAQAccordion>

                    <FAQAccordion title="Where is my landline number?">
                        <div className="flex flex-col">
                            <div className={classes.para}>
                                Landline numbers are automatically removed from your account if you have had zero
                                call minutes for more than two weeks.
                            </div>
                            <div className={classes.para}>
                                You may be able to recover your old landline number.
                            </div>
                        </div>
                    </FAQAccordion>

                    <FAQAccordion title="Recovering a previous number">
                        <div className="flex flex-col">
                            <div className={classes.para}>
                                To recover a previous landline number, tap the <strong>Add Number</strong> button under the My
                                Numbers section on the Home page, or tap the button below.
                            </div>
                            <div className={classes.para}>
                                If your previous landline number is available, you will see
                                a button labelled <strong>Recover a Previous Number</strong>. If you do not see the
                                button, then your previous number is not available.
                            </div>
                            <div className={classes.para}>
                                Tap the <strong>Recover a Previous Number</strong> button and your first available
                                previous number will be selected. Expand <strong>Show other available landline numbers
                                you've had before</strong> to see a list of all available previous numbers.
                            </div>
                            <div className={classes.para}>
                                The one-time landline number <span className={classes.emphasis}>$9</span> set up fee applies when
                                recovering a previous number.
                            </div>
                            <div>
                                <Button
                                    component={RouteLink}
                                    variant="contained"
                                    color="secondary"
                                    to="/transfer"
                                >
                                    Add A Landline Number
                                </Button>
                            </div>

                        </div>
                    </FAQAccordion>
                </div>
            </FuseAnimate>

            <FuseAnimate animation="transition.expandIn" delay={400}>
                <div id="hc-billing" className="mb-6">
                    <Typography variant="h6">Call Minutes</Typography>

                    <FAQAccordion title="How do I roll over my call minutes?">
                        <div className="flex flex-col">
                            <div className={classes.para}>
                                Call minutes are automatically rolled over when you recharge.
                            </div>
                            <div className={classes.para}>
                                For example, you purchase an "XL" recharge on the first of the month. The call minutes will expire on the 28th of the month if you haven't used them before.
                                On the 26th of the month you still have 245 minutes remaining and you're unlikely to use them all before they expire.
                            </div>
                            <div className={classes.para}>
                                Any recharge purchased before your call minutes expire will roll over your remaining minutes and extend their expiry date.
                            </div>
                            <div className={classes.para}>
                                It's not quite payday and finances are a bit tight, so you purchase a cheaper "M" recharge with 290 call minutes. Your existing 245 call minutes are rolled over to give you a total of
                                535 call minutes with 28 days expiry.
                            </div>
                        </div>

                    </FAQAccordion>

                    <FAQAccordion title="How do I get more call minutes?">
                        <RechargingContent autoRecharge={autoRecharge} accountType={accountType}/>
                    </FAQAccordion>
                </div>
            </FuseAnimate>

        </React.Fragment>
    )
}

const ContactCard = ({accountId}) => {
    const classes = useStyles();

    return (
        <div id={"contact-card"} className={clsx(classes.contactCard, "bg-white rounded shadow p-4 mb-6")}>
                <Logo className={classes.panelLogo} fill="#3f51b5" alt="logo" />
                <div className="flex flex-col sm:flex-row">
                    <div className="mt-3 sm:mt-0">
                        <div>
                            <Call color="action" className="mr-2"/>
                            <Link className={classes.link} href="tel:61399997555" variant="body1">
                                (03) 9999 7555
                            </Link>
                        </div>
                        <div className="flex flex-no-wrap mt-2">
                            <Email color="action" className="mr-2"/>
                            <EmailButton variant="link" title="hello@phonethru.com" accountId={accountId}/>
                        </div>
                    </div>
                </div>
        </div>
    )
}

const MobileNumberChangeContent = ({property}) => {
    const classes = useStyles();

    return (
        <div className="flex flex-col">
            <div className={classes.para}>
                You can't change your the mobile number on this account, but you can create a new account with your
                new mobile number and transfer your landline number to the new account.
            </div>
            <div  className={classes.para}>
                Transferring a landline number to another account is a paid service.
            </div>
            <div  className={classes.para}>
                You can start the transfer process by tapping the landline number on the Home page, then
                tapping <strong>Manage Number</strong> then the <strong>Transfer Number</strong> button on the Service Detail page.<br/>
                You can also go straight to
                the <RouteLink to="/transfer" className={classes.link}>transfer page</RouteLink> with the button below.
            </div>
            <div  className={classes.para}>
                A couple of notes about transferring a landline number:
                <ul className={classes.list}>
                    <li className="mb-2">
                        You'll need to know the password of the receiving account. The receiving account is the
                        mobile number you're transferring to.
                    </li>
                    <li className="mb-2">
                        The landline will transfer to the receiving account with its existing call minutes and
                        expiry.
                    </li>
                </ul>
            </div>
            <div>
                <Button
                    component={RouteLink}
                    variant="contained"
                    color="secondary"
                    to="/transfer"
                >
                    Transfer Landline Number
                </Button>
            </div>
        </div>
    )
}

const RechargingContent = ({autoRecharge, accountType}) => {
    const classes = useStyles();

    return (
        <React.Fragment>
            <div className={classes.para}>
                When your call minutes are getting low you'll see a <strong>RECHARGE</strong> button below the
                number on the <RouteLink to="/home" className={classes.link}>home page</RouteLink> or you can
                use the <RouteLink to="/recharge" className={classes.link}> recharge page</RouteLink> at any time.
            </div>
            <div>
                <Button
                    component={RouteLink}
                    variant="contained"
                    color="secondary"
                    to="/recharge"
                >
                    Recharge Now
                </Button>
            </div>
        </React.Fragment>
    )
}

const EmailButton = ({variant, accountId, title="Email Now"}) => {
    const classes = useStyles();

    const mailToMove    = `mailto:hello@phonethru.com?subject=Number%20location%20query%20(Account%20${accountId})`
    const mailToAdd     = `mailto:hello@phonethru.com?subject=Additional%20landline%20number%20(Account%20${accountId})`
    const mailToSupport = `mailto:hello@phonethru.com?subject=Support%20query%20(Account%20${accountId})`
    const mailToNumber  = `mailto:hello@phonethru.com?subject=Number%20Change%20(Account%20${accountId})`

    return (
        <React.Fragment>
            { variant === 'move' ?
                <Button variant="contained" color="secondary" href={mailToMove}>{title}</Button>
                : null
            }
            { variant === 'add' ?
                <Button variant="contained" color="secondary" href={mailToAdd}>{title}</Button>
                : null
            }
            { variant === 'number-change' ?
                <Button variant="contained" color="secondary" href={mailToNumber}>{title}</Button>
                : null
            }
            { variant === 'link' ?
                <Link className={classes.link} href={mailToSupport} variant="body1">
                    {title}
                </Link>
                : null
            }
        </React.Fragment>
    )
}

export default SupportPage;